import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, ShoppingCart, Boxes, UserCheck, TrendingUp, AlertTriangle } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api, formatINR } from "@/lib/api";
import { hasAdminSession } from "@/lib/session";

const STATUS_LABELS = {
  pending_approval: "Pending Approval", approved: "Approved", processing: "Processing",
  payment_confirmed: "Processing", shipped: "Shipped", completed: "Completed", rejected: "Rejected", cancelled: "Cancelled",
};

const KpiCard = ({ icon: Icon, label, value, testid, accent, onClick }) => (
  <Card
    data-testid={testid}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    className={`glass-card p-4 transition-transform ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]" : ""}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
    </div>
    <div className="font-mono text-2xl mt-2">{value}</div>
    {onClick && <div className="text-[11px] text-[hsl(var(--primary))] mt-1">View details →</div>}
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [crm, setCrm] = useState(null);

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => navigate("/admin/login"));
    api.get("/crm/dashboard").then(({ data }) => setCrm(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!data) return <AdminShell title="Dashboard"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;
  const k = data.kpis;

  return (
    <AdminShell title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="kpi-revenue" icon={IndianRupee} label="Total Revenue" value={formatINR(k.total_revenue)} accent="bg-[#E7F3EA] text-[#1F5B2E]" onClick={() => navigate("/crm/orders")} />
        <KpiCard testid="kpi-open-orders" icon={ShoppingCart} label="Open Orders" value={k.open_orders} accent="bg-[#EAF1F7] text-[#1E3E5C]" onClick={() => navigate("/crm/orders")} />
        <KpiCard testid="kpi-stock-value" icon={Boxes} label="Stock Value" value={formatINR(k.stock_value)} accent="bg-[#FFF7E6] text-[#8E6F33]" onClick={() => navigate("/admin/inventory")} />
        <KpiCard testid="kpi-pending-approvals" icon={UserCheck} label="Pending Approvals" value={k.pending_approvals} accent="bg-[#FBF1DF] text-[#7A4B12]" onClick={() => navigate("/admin/customers?filter=pending")} />
      </div>

      {crm && (
        <div className="mt-4" data-testid="dashboard-crm-metrics">
          <h3 className="font-medium mb-3 text-sm text-muted-foreground uppercase tracking-wide">Follow-Up Overview</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <KpiCard testid="crm-open-leads" icon={IndianRupee} label="Open Leads" value={crm.open_leads} accent="bg-[#EDE7F7] text-[#4B2E7A]" onClick={() => navigate("/crm/leads")} />
            <KpiCard testid="crm-followups-overdue" icon={ShoppingCart} label="Follow-Ups Overdue" value={crm.followups_overdue} accent="bg-[#FBE7E7] text-[#7A1F1F]" onClick={() => navigate("/crm/daily-tasks?tab=customer")} />
            <KpiCard testid="crm-due-today" icon={Boxes} label="Cust. Due Today" value={crm.customer_followups_due_today} accent="bg-[#FBF1DF] text-[#7A4B12]" onClick={() => navigate("/crm/daily-tasks?tab=customer")} />
            <KpiCard testid="crm-critical-delays" icon={AlertTriangle} label="Critical Delays" value={crm.critical_delays} accent="bg-[#FBE7E7] text-[#7A1F1F]" onClick={() => navigate("/crm/daily-tasks?tab=delays")} />
            <KpiCard testid="crm-lead-due-today" icon={IndianRupee} label="Lead Due Today" value={crm.lead_followups_due_today} accent="bg-[#EAF1F7] text-[#1E3E5C]" onClick={() => navigate("/crm/daily-tasks?tab=lead")} />
            <KpiCard testid="crm-gold-month" icon={Boxes} label="Gold Sold (mo)" value={`${crm.gold_sold_month_g} g`} accent="bg-[#FFF7E6] text-[#8E6F33]" onClick={() => navigate("/crm/orders")} />
            <KpiCard testid="crm-inactive" icon={ShoppingCart} label="Inactive Customers" value={crm.inactive_customers} accent="bg-[#F0E9E9] text-[#5A4A4A]" onClick={() => navigate("/crm/winback")} />
            <KpiCard testid="crm-winback" icon={UserCheck} label="Win-Back Candidates" value={crm.winback_candidates} accent="bg-[#E7F3EA] text-[#1F5B2E]" onClick={() => navigate("/crm/winback")} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-medium">Sales Trend</h3></div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.sales_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Line type="monotone" dataKey="sales" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4"><Boxes className="h-4 w-4 text-[hsl(var(--primary))]" /><h3 className="font-medium">Inventory by Category</h3></div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.inventory_by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="category" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <Card className="glass-card p-5">
          <h3 className="font-medium mb-4">Recent Orders</h3>
          {data.recent_orders.length === 0 ? <p className="text-sm text-muted-foreground">No orders yet.</p> : (
            <div className="space-y-2">
              {data.recent_orders.map((o) => (
                <div key={o.id} onClick={() => navigate("/crm/orders")} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0 cursor-pointer hover:bg-[#F7F0E6] rounded px-1 -mx-1">
                  <div><span className="font-mono">{o.order_no}</span><span className="text-muted-foreground ml-2">{o.shop_name}</span></div>
                  <div className="flex items-center gap-3"><Badge variant="secondary">{STATUS_LABELS[o.status] || o.status}</Badge><span className="font-mono">{formatINR(o.total)}</span></div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="glass-card p-5">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-4 w-4 text-[#7A4B12]" /><h3 className="font-medium">Low Stock Alerts</h3></div>
          {data.low_stock.length === 0 ? <p className="text-sm text-muted-foreground">All items well stocked.</p> : (
            <div className="space-y-2">
              {data.low_stock.map((p) => (
                <div key={p.id} onClick={() => navigate("/admin/inventory")} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0 cursor-pointer hover:bg-[#F7F0E6] rounded px-1 -mx-1">
                  <div><span>{p.name}</span><span className="text-muted-foreground ml-2 font-mono text-xs">{p.sku}</span></div>
                  <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">{p.stock} left</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </AdminShell>
  );
}
